import { cryptoService } from '@/services/crypto.service';
import { getKeys, storeKeys, getActiveKey, deleteKey } from '../../services/encryption.service';

const state = {
  isInitialized: false,
  keyVersion: null,
  error: null,
  keys: [],
  password: null
};

const mutations = {
  SET_INITIALIZED(state, value) {
    state.isInitialized = value;
    value ? sessionStorage.setItem('encryptionInitialized', value) : sessionStorage.removeItem('encryptionInitialized');
  },
  SET_KEY_VERSION(state, version) {
    state.keyVersion = version;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_KEYS(state, keys) {
    state.keys = keys;
  },
  SET_PASSWORD(state, password) {
    state.password = password;
  }
};

const actions = {
  /**
   * Initializes encryption with existing keys or creates new ones
   * @param {Object} context - Vuex context
   * @param {Object} payload - Contains password for key decryption
   */
  async initializeEncryption({ commit }, { password }) {
    try {
      commit('SET_PASSWORD', password);

      const response = await getKeys();
      const keys = response.keys;
    
      if (!keys || keys.length === 0 || sessionStorage.getItem('needsNewEncryption')) {
        const keyPair = await cryptoService.generateKeyPair();
        const publicKey = await cryptoService.exportPublicKey(keyPair.publicKey);
        const encryptedPrivateKey = await cryptoService.encryptPrivateKey(keyPair.privateKey, password);

        const newKey = await storeKeys({publicKey, encryptedPrivateKey: JSON.stringify(encryptedPrivateKey)});
        console.log('[Encryption Store] New key created:', newKey);
        
        const keyId = newKey.keyId || newKey.id;
        cryptoService.setKeys(keyPair.privateKey, keyPair.publicKey, keyId);
        console.log('[Encryption Store] Keys set in cryptoService:', { keyId: keyId });

        commit('SET_KEY_VERSION', keyId);
        commit('SET_INITIALIZED', true);
        commit('SET_ERROR', null);
        sessionStorage.removeItem('needsNewEncryption');
        
        console.log('[Encryption Store] State after initialization:', {
            keyVersion: keyId,
            isInitialized: true
        });
        
        // Add a small delay to ensure state is fully propagated
        await new Promise(resolve => setTimeout(resolve, 100));
        return;
      }

      const sortedKeys = [...keys].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const latestKey = sortedKeys[0];
      console.log('[Encryption Store] Loading existing key:', latestKey);
      
      const encryptedPrivateKey = JSON.parse(latestKey.encryptedPrivateKey);
      const privateKey = await cryptoService.decryptPrivateKey(encryptedPrivateKey, password);

      const publicKeyBuffer = cryptoService.base64ToArrayBuffer(latestKey.publicKey);
      const importedPublicKey = await window.crypto.subtle.importKey("spki", publicKeyBuffer, { name: "RSA-OAEP", hash: "SHA-256" }, true, ["encrypt"]);
      
      const keyId = latestKey.keyId || latestKey.id;
      cryptoService.setKeys(privateKey, importedPublicKey, keyId);
      
      commit('SET_KEY_VERSION', keyId);
      commit('SET_INITIALIZED', true);
      commit('SET_ERROR', null);
      
      console.log('[Encryption Store] State after loading existing key:', {
        keyVersion: keyId,
        isInitialized: true
      });
      
      // Add a small delay to ensure state is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));
      return;
      
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  /**
   * Registers new encryption keys
   * @param {Object} context - Vuex context
   * @param {Object} payload - Contains password for key encryption
   */
  async registerNewKeys({ commit }, { password }) {
    try {
      const keyPair = await cryptoService.generateKeyPair(); // Generate new key pair
      const publicKey = await cryptoService.exportPublicKey(keyPair.publicKey); // Export public key
      const encryptedPrivateKey = await cryptoService.encryptPrivateKey(keyPair.privateKey, password); // Encrypt private key
      
      const newKey = await storeKeys({ publicKey, encryptedPrivateKey: JSON.stringify(encryptedPrivateKey) });
      const keyId = newKey.keyId || newKey.id;
      cryptoService.setKeys(keyPair.privateKey, keyPair.publicKey, keyId);
      
      commit('SET_KEY_VERSION', keyId);
      commit('SET_INITIALIZED', true);
      commit('SET_ERROR', null);
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  /**
   * Updates the unlock password by rotating keys and re-encrypting files
   * @param {Object} context - Vuex context
   * @param {Object} payload - Contains old and new passwords
   */
  async updateUnlockPassword({ commit, state }, { oldPassword, newPassword }) {
    try {
      const keys = await getKeys();
      if (!keys || keys.length === 0) {
        throw new Error('No encryption keys found');
      }

      const sortedKeys = [...keys].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const currentKey = keys.find(k => k.id === state.keyVersion) || sortedKeys[0];
      if (!currentKey) {
        throw new Error('No active key found');
      }

      const encryptedPrivateKey = JSON.parse(currentKey.encryptedPrivateKey);
      const privateKey = await cryptoService.decryptPrivateKey(encryptedPrivateKey, oldPassword);
      const newEncryptedPrivateKey = await cryptoService.encryptPrivateKey(privateKey, newPassword);
      const publicKeyBuffer = cryptoService.base64ToArrayBuffer(currentKey.publicKey);
      const importedPublicKey = await window.crypto.subtle.importKey(
        "spki",
        publicKeyBuffer,
        { name: "RSA-OAEP", hash: "SHA-256" },
        true,
        ["encrypt"]
      );

      await storeKeys({ publicKey: currentKey.publicKey, encryptedPrivateKey: JSON.stringify(newEncryptedPrivateKey) });
      cryptoService.setKeys(privateKey, importedPublicKey, state.keyVersion);
      commit('SET_PASSWORD', newPassword);
      commit('SET_ERROR', null);
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  /**
   * Clears encryption state
   * Used during logout or session end
   */
  clearEncryption({ commit }) {
    console.log('🔐 Clearing encryption state...');
    
    // Clear keys from memory
    cryptoService.clearKeys();
    
    // Clear all state
    commit('SET_INITIALIZED', false);
    commit('SET_KEY_VERSION', null);
    commit('SET_ERROR', null);
    commit('SET_KEYS', []);
    commit('SET_PASSWORD', null);
    
    // Clear from session storage
    sessionStorage.removeItem('encryptionInitialized');
    sessionStorage.removeItem('encryptionDerivedKey');
    sessionStorage.removeItem('needsNewEncryption');
    sessionStorage.removeItem('redirectAfterUnlock');
    
    // Clear from local storage
    localStorage.removeItem('encryptionInitialized');
    
    // Clear any MSAL-related storage that might persist
    sessionStorage.removeItem('msal.interaction.status');
    sessionStorage.removeItem('msal.error');
    sessionStorage.removeItem('msal.error.description');
    
    console.log('✅ Encryption state cleared');
  },

  /**
   * Restores encryption from sessionStorage (using derived key)
   * @param {Object} context - Vuex context
   */
  async restoreEncryptionFromSession({ commit }) {
    const rawKeyBase64 = sessionStorage.getItem('encryptionDerivedKey');
    if (!rawKeyBase64) return false;

    const activeKey = await getActiveKey();
    if (!activeKey) return false;

    const encryptedPrivateKey = JSON.parse(activeKey.encryptedPrivateKey);
    const rawKey = cryptoService.base64ToArrayBuffer(rawKeyBase64);

    // Import the derived key
    const derivedKey = await window.crypto.subtle.importKey(
      'raw',
      rawKey,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    // Decrypt private key
    const privateKey = await cryptoService.decryptPrivateKeyWithDerivedKey(encryptedPrivateKey, derivedKey);

    // Import public key
    const publicKeyBuffer = cryptoService.base64ToArrayBuffer(activeKey.publicKey);
    const importedPublicKey = await window.crypto.subtle.importKey(
      "spki",
      publicKeyBuffer,
      { name: "RSA-OAEP", hash: "SHA-256" },
      true,
      ["encrypt"]
    );

    cryptoService.setKeys(privateKey, importedPublicKey, activeKey.keyId);
    commit('SET_KEY_VERSION', activeKey.keyId);
    commit('SET_INITIALIZED', true);
    commit('SET_ERROR', null);
    return true;
  },

  /**
   * Unlocks encryption
   * @param {Object} context - Vuex context
   * @param {Object} payload - Contains password for decryption
   */
  async unlockEncryption({ commit }, { password }) {
    try {
      commit('SET_PASSWORD', password);
      const activeKey = await getActiveKey();
      if (!activeKey) {
        throw new Error('No active encryption key found');
      }

      // Decrypt private key to test password
      const encryptedPrivateKey = JSON.parse(activeKey.encryptedPrivateKey);
      const salt = encryptedPrivateKey.salt;
      // Derive key and store in sessionStorage
      const derivedKey = await cryptoService.deriveKeyFromPassword(password, cryptoService.base64ToArrayBuffer(salt));
      const rawKey = await window.crypto.subtle.exportKey('raw', derivedKey);
      const rawKeyBase64 = cryptoService.arrayBufferToBase64(rawKey);
      sessionStorage.setItem('encryptionDerivedKey', rawKeyBase64);

      const privateKey = await cryptoService.decryptPrivateKey(encryptedPrivateKey, password);

      // Import public key
      const publicKeyBuffer = cryptoService.base64ToArrayBuffer(activeKey.publicKey);
      const importedPublicKey = await window.crypto.subtle.importKey(
        "spki",
        publicKeyBuffer,
        {
          name: "RSA-OAEP",
          hash: "SHA-256",
        },
        true,
        ["encrypt"]
      );

      // Store keys in memory
      cryptoService.setKeys(privateKey, importedPublicKey, activeKey.keyId);
      commit('SET_KEY_VERSION', activeKey.keyId);
      commit('SET_INITIALIZED', true);
      commit('SET_ERROR', null);
      
      // Add a small delay to ensure state is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  /**
   * Fetches user's encryption keys
   * @param {Object} context - Vuex context
   */
  async fetchKeys({ commit }) {
    try {
      const keys = await getKeys();
      commit('SET_KEYS', keys || []);
      commit('SET_ERROR', null);
    } catch (error) {
      // If the error is about no keys found, just set an empty array
      if (error.message && error.message.includes('KEYS_NOT_FOUND')) {
        commit('SET_KEYS', []);
        commit('SET_ERROR', null);
        return;
      }
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  /**
   * Deletes a user's encryption key
   * @param {Object} context - Vuex context
   * @param {string} keyId - ID of the key to delete
   */
  async deleteKey({ commit }, keyId) {
    try {
      if (!keyId || typeof keyId !== 'string') {
        throw new Error('Valid key ID is required');
      }
      await deleteKey(keyId);
      commit('SET_ERROR', null);
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  /**
   * Generates a new encryption key pair
   * @param {Object} context - Vuex context
   * @param {Object} payload - Contains password for key encryption
   */
  async generateNewKey({ commit, state }, { password }) {
    try {
      const keyPair = await cryptoService.generateKeyPair();
      const publicKey = await cryptoService.exportPublicKey(keyPair.publicKey);
      const encryptedPrivateKey = await cryptoService.encryptPrivateKey(keyPair.privateKey, password);
     
      const newKey = await storeKeys({ publicKey, encryptedPrivateKey: JSON.stringify(encryptedPrivateKey) });
      commit('SET_KEYS', [...state.keys, newKey]);

      const newVersion = newKey.keyId || newKey.id;
      commit('SET_KEY_VERSION', newVersion);
      
      cryptoService.setKeys(keyPair.privateKey, keyPair.publicKey, newVersion);

      commit('SET_ERROR', null);

    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  }
};

const getters = {
  isEncryptionInitialized: state => state.isInitialized,
  encryptionError: state => state.error,
  userKeys: state => state.keys
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}; 